import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";

const links = [
  { label: "About", to: "/#about" },
  { label: "Services", to: "/#services" },
  { label: "What We Do", to: "/#what-we-do" },
  { label: "Residential", to: "/residential" },
];

export default function Nav() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  return (
    <header className="fixed top-0 inset-x-0 z-50 bg-paper/90 backdrop-blur-md border-b border-ink/10" data-testid="main-nav">
      <div className="flex items-stretch justify-between px-6 md:px-12 h-16 md:h-20">
        <Link to="/" className="flex items-center gap-3" data-testid="nav-logo" onClick={() => setOpen(false)}>
          <img src="/logo.png" alt="Rogers American Construction logo" className="w-9 h-9 md:w-11 md:h-11 rounded-full object-cover" data-testid="nav-logo-image" />
          <span className="font-display font-semibold uppercase tracking-tight text-base md:text-lg leading-none">
            Rogers American
            <span className="block text-[10px] md:text-xs font-sans font-bold tracking-[0.3em] text-smoke">Construction</span>
          </span>
        </Link>
        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <Link
              key={l.label}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className={`text-sm font-semibold uppercase tracking-widest hover:text-accent transition-colors ${
                pathname === l.to ? "text-accent" : "text-ink"
              }`}
            >
              {l.label}
            </Link>
          ))}
          <Link
            to="/request-quote"
            data-testid="nav-quote-button"
            className="group inline-flex items-center gap-2 bg-ink text-paper text-sm font-semibold uppercase tracking-widest px-6 py-3 hover:bg-accent transition-colors"
          >
            Request a Quote
            <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </nav>
        <button
          className="md:hidden flex items-center"
          onClick={() => setOpen(!open)}
          data-testid="mobile-menu-button"
          aria-label="Toggle menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="md:hidden overflow-hidden border-t border-ink/10 bg-paper"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col px-6 py-6 gap-5">
              {links.map((l) => (
                <Link
                  key={l.label}
                  to={l.to}
                  onClick={() => setOpen(false)}
                  className="font-display text-2xl uppercase tracking-tight"
                  data-testid={`mobile-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                >
                  {l.label}
                </Link>
              ))}
              <Link
                to="/request-quote"
                onClick={() => setOpen(false)}
                className="mt-2 inline-flex items-center justify-center gap-2 bg-ink text-paper text-sm font-semibold uppercase tracking-widest px-6 py-4"
                data-testid="mobile-quote-button"
              >
                Request a Quote <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
